// Main menu, name prompt and mode select.
import { CONFIG } from '../config.ts';
import { deckStats } from '../shared/deck.ts';
import { CARD_DB, DATA_WARNINGS } from './data.ts';
import { cardBackSrc, menuArtSrc, setImg } from './images.ts';
import { session } from './session.ts';
import { NAME_RULES, checkName, claimName, logIn, logOut, nameProblem, playAsGuest, playOffline } from './accounts.ts';
import { el, button, toast, pickTextFile } from './ui.ts';
import { MATCH_EXTENSION, parseMatchFile } from '../shared/match.ts';
import { showDeckBuilder } from './deckBuilder.ts';
import { startTable } from './table/tableScreen.ts';
import { OnlineConnection, forgetOnlineGame, savedOnlineGame, watchOpenGames } from './table/connection.ts';
import { chatBox, type ChatBox } from './chat.ts';
import { CODE_LENGTH, NEED_DECK, cleanCode } from '../shared/online.ts';
import type { Seat } from '../shared/engine.ts';
import { hasNews, latestNews, showNews } from './news.ts';
import { hasRules, showRules } from './rules.ts';
import { BOTS } from '../shared/bot/botPlay.ts';

const app = () => document.getElementById('app')!;

export function showScreen(node: Node) {
  app().replaceChildren(node);
  window.scrollTo(0, 0);
}

function deckLine() {
  if (!session.savedDeck) return 'No deck saved yet — build one in the Deck Builder.';
  const { main } = deckStats(session.savedDeck, CARD_DB);
  const emperorNum = [...session.savedDeck.keys()].find((n) => CARD_DB.byNumber.get(n)?.types.includes('emperor'));
  const emperor = emperorNum !== undefined ? CARD_DB.byNumber.get(emperorNum)!.name : 'no Emperor';
  return `Your deck: ${session.savedDeckName} — ${emperor} + ${main} cards`;
}

function menuShell(...children: Node[]) {
  const fan = el(
    'div',
    { class: 'menu-fan', 'aria-hidden': 'true' },
    [0, 1, 2].map((i) => setImg(el('img', { class: `fan-card fan-${i}`, alt: '' }), cardBackSrc())),
  );
  const screen = el(
    'main',
    { class: 'menu-screen art-pending' },
    fan,
    el('div', { class: 'menu-panel' }, el('h1', { class: 'menu-title' }, CONFIG.gameTitle), ...children),
  );
  // the menu picture (public/menu/menu.png) fills the screen behind the buttons; without one, the fan of card backs shows
  void menuArtSrc().then((src) => {
    screen.classList.remove('art-pending');
    if (!src) return;
    screen.classList.add('has-art');
    // a full address: a bare "menu/menu.png" inside the stylesheet would be looked for beside the stylesheet
    screen.style.setProperty('--menu-art', `url("${new URL(src, location.href).href}")`);
  });
  return screen;
}

export function showMainMenu() {
  const warnings = DATA_WARNINGS.length
    ? el(
        'details',
        { class: 'data-warnings' },
        el('summary', {}, `${DATA_WARNINGS.length} problem${DATA_WARNINGS.length === 1 ? '' : 's'} in the card or token spreadsheet`),
        el('ul', {}, DATA_WARNINGS.map((w) => el('li', {}, w))),
      )
    : null;
  showScreen(
    menuShell(
      el(
        'nav',
        { class: 'menu-buttons' },
        button('Play', () => showNamePrompt(), 'btn-big btn-primary'),
        button('Deck Builder', () => showDeckBuilder(showMainMenu), 'btn-big'),
        ...(hasRules ? [button('Rules', () => showRules(), 'btn-big', { title: 'How to play: turns, cards, status effects' })] : []),
        button('Watch a Replay', () => void loadMatch(), 'btn-big', { title: 'Watch a finished game again from its replay file' }),
        // the changelog: whatever is written in data/news.txt
        ...(hasNews ? [el('button', { class: 'btn btn-big btn-news', type: 'button', title: 'What is new, what changed, and balance updates', onclick: () => showNews() }, 'News & Updates', latestNews ? el('span', { class: 'news-latest' }, `Latest: ${latestNews}`) : null)] : []),
        button('Play Tutorial', () => playTutorial(), 'btn-big', { title: 'A game against Martin with hints along the way: good for your first game' }),
      ),
      el('p', { class: 'menu-foot' }, deckLine()),
      ...(session.account ? [el('p', { class: 'menu-foot account-foot' }, 'Signed in as ', el('strong', {}, session.account.name), ' · ', el('button', { class: 'link-button', type: 'button', onclick: () => void logOut().then(() => { toast('Logged out.', 'info'); showMainMenu(); }) }, 'Log out'))] : []),
      ...(warnings ? [warnings] : []),
    ),
  );
}

/**
 * Who's playing: an account (a name claimed with a password), or a guest. Signed in already on this browser: say so,
 * and go on. Otherwise a name: free names are claimed with a new password, taken ones need theirs; or Play as Guest.
 */
function showNamePrompt(then: () => void = showModeSelect) {
  if (session.account) {
    const name = session.account.name;
    showScreen(
      menuShell(
        el('h2', { class: 'menu-sub' }, 'Who’s playing?'),
        el('p', { class: 'account-line' }, 'Signed in as ', el('strong', {}, name)),
        el(
          'div',
          { class: 'menu-buttons' },
          button(`Continue as ${name}`, () => then(), 'btn-big btn-primary'),
          button('Switch account', () => void logOut().then(() => showNamePrompt(then)), 'btn-big'),
          button('Back', showMainMenu, 'btn-ghost'),
        ),
      ),
    );
    return;
  }
  const input = el('input', {
    id: 'player-name',
    class: 'text-input',
    maxlength: String(NAME_RULES.max),
    placeholder: 'Your player name',
    autocomplete: 'username',
    value: session.guest ? '' : session.playerName,
  });
  const error = el('p', { class: 'form-error', role: 'alert' });
  let busy = false;
  const go = async () => {
    if (busy) return;
    const name = input.value.trim();
    const bad = nameProblem(name);
    if (bad) { error.textContent = bad; input.focus(); return; }
    busy = true;
    const r = await checkName(name);
    busy = false;
    if (!r.ok && r.offline) {
      // the page was opened without the game server (npm run dev): no accounts to check, so the name is used as typed
      playOffline(name);
      toast(`No game server here, so accounts are off: playing offline as ${name}.`, 'info');
      then();
      return;
    }
    if (!r.ok) { error.textContent = r.error; return; }
    showPasswordPrompt(r.taken ? 'login' : 'claim', r.name ?? name, then);
  };
  input.addEventListener('input', () => (error.textContent = ''));
  input.addEventListener('keydown', (e) => e.key === 'Enter' && void go());
  showScreen(
    menuShell(
      el('h2', { class: 'menu-sub' }, 'Who’s playing?'),
      el('label', { class: 'field-label', for: 'player-name' }, 'Player name'),
      input,
      el('p', { class: 'field-note' }, `${NAME_RULES.min} to ${NAME_RULES.max} letters or numbers. The first player to use a name claims it with a password; after that, it’s theirs.`),
      error,
      el(
        'div',
        { class: 'menu-buttons' },
        button('Continue', () => void go(), 'btn-big btn-primary'),
        button('Play as Guest', () => { playAsGuest(); toast(`Playing as ${session.playerName}. Your cards still count toward card stats; a guest keeps no account stats.`, 'info'); then(); }, 'btn-big', { title: 'No account: you play as “Player” and a few numbers' }),
        button('Back', showMainMenu, 'btn-ghost'),
      ),
    ),
  );
  input.focus();
}

/** A password: choosing one to claim a free name, or entering the one a taken name already has. */
function showPasswordPrompt(mode: 'claim' | 'login', name: string, then: () => void) {
  const claim = mode === 'claim';
  const pass = el('input', { id: 'player-pass', class: 'text-input', type: 'password', maxlength: '64', autocomplete: claim ? 'new-password' : 'current-password', placeholder: claim ? 'Choose a password' : 'Password' });
  const again = claim ? el('input', { id: 'player-pass2', class: 'text-input', type: 'password', maxlength: '64', autocomplete: 'new-password', placeholder: 'Type it again' }) : null;
  const keep = el('input', { id: 'keep-signed', type: 'checkbox', checked: true }) as HTMLInputElement;
  const error = el('p', { class: 'form-error', role: 'alert' });
  // (a hidden name field, so a browser's password manager knows which account the password is for)
  const userField = el('input', { type: 'text', autocomplete: 'username', value: name, hidden: true, readonly: true, tabindex: '-1', 'aria-hidden': 'true' });
  let busy = false;
  const go = async () => {
    if (busy) return;
    const pw = pass.value;
    if (pw.length < NAME_RULES.passwordMin) { error.textContent = `A password needs at least ${NAME_RULES.passwordMin} characters.`; pass.focus(); return; }
    if (again && again.value !== pw) { error.textContent = 'The two passwords don’t match.'; again.focus(); return; }
    busy = true;
    submit.disabled = true;
    const r = claim ? await claimName(name, pw, keep.checked) : await logIn(name, pw, keep.checked);
    busy = false;
    submit.disabled = false;
    if (!r.ok) { error.textContent = r.error; pass.select(); return; }
    toast(claim ? `${session.playerName} is yours now. Welcome!` : `Welcome back, ${session.playerName}.`, 'info');
    then();
  };
  for (const f of [pass, again]) {
    f?.addEventListener('input', () => (error.textContent = ''));
    f?.addEventListener('keydown', (e) => e.key === 'Enter' && void go());
  }
  const submit = button(claim ? `Claim ${name}` : 'Log in', () => void go(), 'btn-big btn-primary');
  showScreen(
    menuShell(
      el('h2', { class: 'menu-sub' }, claim ? `${name} is free` : `Welcome back, ${name}`),
      el('p', { class: 'field-note' }, claim ? 'Choose a password to make this name yours. You’ll need it to play as this name on another browser or computer.' : 'This name belongs to an account. Enter its password to play as it.'),
      userField,
      el('label', { class: 'field-label', for: 'player-pass' }, 'Password'),
      pass,
      ...(again ? [el('label', { class: 'field-label', for: 'player-pass2' }, 'Password again'), again] : []),
      el('label', { class: 'check-row', for: 'keep-signed' }, keep, ' Keep me signed in on this browser'),
      error,
      el('div', { class: 'menu-buttons' }, submit, button(claim ? 'Use another name' : 'Not you? Use another name', () => showNamePrompt(then), 'btn-ghost')),
    ),
  );
  pass.focus();
}

function showModeSelect() {
  showScreen(
    menuShell(
      el('h2', { class: 'menu-sub' }, `Welcome, ${session.playerName}`),
      el(
        'nav',
        { class: 'menu-buttons' },
        modeButton('Solo Table', 'Practice alone and try out decks', startSolo, false),
        modeButton('1v1', 'One against one, online', () => showVersusMenu(false), true),
        modeButton('2v2', 'Two teams of two, online', () => showVersusMenu(true), true),
        soonButton('3v3', 'Three teams of three'),
        soonButton('Boss Fight', 'Take on a boss together'),
      ),
      el('p', { class: 'menu-foot' }, deckLine()),
      el('div', { class: 'menu-buttons' }, button('Back', () => showNamePrompt(), 'btn-ghost')),
    ),
  );
}

function soonButton(title: string, sub: string) {
  return el('button', { class: 'btn btn-big btn-mode btn-soon', type: 'button', disabled: true, title: 'Coming soon' }, el('span', { class: 'mode-title' }, title, el('span', { class: 'soon-tag' }, 'Coming soon')), el('span', { class: 'mode-sub' }, sub));
}
/** The tutorial: a game against Martin with hints along the way (it needs a player name and a deck first). */
function playTutorial() {
  if (!session.playerName) {
    showNamePrompt(() => playTutorial());
    return;
  }
  startBot('easy', true);
}

function modeButton(title: string, sub: string, onClick: () => void, primary: boolean) {
  return el(
    'button',
    { class: `btn btn-big btn-mode ${primary ? 'btn-primary' : ''}`, type: 'button', onclick: onClick },
    el('span', { class: 'mode-title' }, title),
    el('span', { class: 'mode-sub' }, sub),
  );
}

// ---------------- Online games: 1v1 and 2v2 ----------------
// The 1v1 and 2v2 buttons open the same menu, each for its own kind of game: its Open Games list shows only
// that kind, and Create Game makes that kind. Join with a Code joins whatever the code is for.
let versusTeams = false;

let stopWatching: (() => void) | null = null;
function stopLobby() {
  stopWatching?.();
  stopWatching = null;
}

function showVersusMenu(teams = versusTeams) {
  versusTeams = teams;
  stopLobby();
  const saved = savedOnlineGame();
  const list = el('ul', { class: 'open-games' }, el('li', { class: 'muted' }, 'Looking for open games…'));
  stopWatching = watchOpenGames((games) => {
    if (games === null) {
      list.replaceChildren(el('li', { class: 'muted' }, 'Can’t reach the game server. Online games work when the game is opened from its online address (or started with  npm run online ).'));
      return;
    }
    const mine = games.filter((g) => !!g.teams === teams);
    list.replaceChildren(
      ...(mine.length
        ? mine.map((g) =>
            el(
              'li',
              { class: 'open-game' },
              el('span', { class: 'open-game-who' }, el('strong', {}, g.host), el('span', { class: 'muted' }, g.teams ? ` · 2v2 · ${g.seated ?? 1}/4 players` : ` · ${g.emperor}`)),
              button('Join', () => joinGame(g.code), 'btn-primary btn-small'),
            ),
          )
        : [el('li', { class: 'muted' }, `No open ${teams ? '2v2' : '1v1'} games right now. Make one, and it shows here for your friends.`)]),
    );
  });
  showScreen(
    menuShell(
      el('h2', { class: 'menu-sub' }, teams ? '2v2' : '1v1'),
      el('p', { class: 'muted' }, teams ? 'Two teams of two: four players online.' : 'You against one friend online.'),
      ...(saved
        ? [el(
            'div',
            { class: 'rejoin-box' },
            el('p', {}, `You have a game in progress: ${saved.names.length === 4 ? `${[saved.names[0], saved.names[2]].filter(Boolean).join(' & ')} vs ${[saved.names[1], saved.names[3]].filter(Boolean).join(' & ')}` : saved.names.filter(Boolean).join(' vs ')} (code ${saved.code}).`),
            el('div', { class: 'menu-buttons' }, button('Rejoin Game', () => rejoinGame(), 'btn-big btn-primary'), button('Forget it', () => { forgetOnlineGame(); showVersusMenu(); }, 'btn-ghost')),
          )]
        : []),
      el('nav', { class: 'menu-buttons' }, button('Create Game', () => showCreateGame(), `btn-big ${saved ? '' : 'btn-primary'}`), button('Join with a Code', () => showJoinByCode(), 'btn-big')),
      el('h3', { class: 'menu-sub-small' }, 'Open games'),
      list,
      el('p', { class: 'menu-foot' }, deckLine()),
      el('div', { class: 'menu-buttons' }, ...(hasRules ? [button('Rules', () => showRules(), '')] : []), button('Back', () => { stopLobby(); showModeSelect(); }, 'btn-ghost')),
    ),
  );
}

function startBot(level: string, tutorial = false) {
  if (!needDeck(() => startBot(level, tutorial))) return;
  const conn = new OnlineConnection({ bot: { name: session.playerName, deck: session.savedDeck!, level, ...(tutorial ? { tutorial: true } : {}) } });
  connecting(conn, tutorial ? 'Setting up the tutorial…' : 'Setting up the table…');
}

function showCreateGame() {
  if (!needDeck(showCreateGame)) return;
  const listed = el('input', { type: 'checkbox', id: 'list-game', checked: true });
  showScreen(
    menuShell(
      el('h2', { class: 'menu-sub' }, versusTeams ? 'Create a 2v2 Game' : 'Create a 1v1 Game'),
      el('label', { class: 'check-row', for: 'list-game' }, listed, ' Show it under Open Games, so any of your friends can join'),
      el('p', { class: 'muted' }, 'Either way you get a code to send to someone.'),
      el(
        'div',
        { class: 'menu-buttons' },
        button('Create', () => {
          stopLobby();
          const conn = new OnlineConnection({ create: { name: session.playerName, deck: session.savedDeck!, open: (listed as HTMLInputElement).checked, teams: versusTeams } });
          waitForOpponent(conn);
        }, 'btn-big btn-primary'),
        button('Back', () => showVersusMenu(), 'btn-ghost'),
      ),
    ),
  );
}

/** The host waits here, code on screen, until the game can start; then the table opens for everyone. */
function waitForOpponent(conn: OnlineConnection) {
  conn.onError((m) => toast(m, 'error'));
  conn.onClosed((reason) => {
    if (reason !== 'cancelled') toast(reason, 'error');
    showVersusMenu();
  });
  conn.onRoom((started) => (started ? openOnlineTable(conn) : drawLobby(conn)));
  drawLobby(conn);
}

/** The 2v2 lobby's chat box: one per game, kept while the lobby is drawn again and again. */
const lobbyChats = new WeakMap<OnlineConnection, ChatBox>();
function lobbyChat(conn: OnlineConnection): ChatBox {
  let box = lobbyChats.get(conn);
  if (!box) lobbyChats.set(conn, (box = chatBox(conn)));
  return box;
}

/**
 * The waiting room: the game's code and, in a 2v2 game, who sits where. Anyone may move to an empty seat; the
 * host starts the game once all four are taken. Drawn again each time the server says something changed.
 */
function drawLobby(conn: OnlineConnection) {
  const code = conn.code || '·····';
  const copy = button('Copy Code', () => { void navigator.clipboard?.writeText(conn.code).then(() => toast('Code copied.', 'ok')); }, 'btn');
  if (!conn.teams) {
    showScreen(
      menuShell(
        el('h2', { class: 'menu-sub' }, 'Your game'),
        el('div', { class: 'game-code' }, code),
        el('p', { class: 'muted' }, conn.code ? 'Waiting for an opponent… Send them this code, or they can join from Open Games. Or fill the seat with a bot and start now.' : 'Making the game…'),
        ...(conn.code ? [botPicker('Fill the seat with a bot', (level) => conn.fillBot(1, level))] : []),
        el('div', { class: 'menu-buttons' }, copy, button('Cancel', () => conn.cancel(), 'btn-ghost')),
      ),
    );
    return;
  }
  const host = conn.seat === conn.host;
  const full = conn.names.length === 4 && conn.names.every(Boolean);
  const seatRow = (seat: Seat, label: string) => {
    const name = conn.names[seat];
    const isBot = conn.bots.includes(seat);
    const tags = [seat === conn.seat ? 'you' : '', seat === conn.host ? 'host' : '', isBot ? 'bot' : ''].filter(Boolean);
    return el(
      'li',
      { class: `lobby-seat ${seat === conn.seat ? 'is-you' : ''}` },
      el('span', { class: 'lobby-seat-where muted' }, label),
      el('strong', { class: 'lobby-seat-name' }, name || 'Empty seat'),
      tags.length ? el('span', { class: 'muted' }, ` (${tags.join(', ')})`) : '',
      !name ? button('Sit here', () => conn.takeSeat(seat), 'btn-small') : '',
      !name && host ? botSelect((level) => conn.fillBot(seat, level)) : '',
      isBot && host ? button('Remove bot', () => conn.clearBot(seat), 'btn-small btn-ghost') : '',
    );
  };
  const team = (title: string, left: Seat, right: Seat) =>
    el('div', { class: 'lobby-team' }, el('h3', { class: 'menu-sub-small' }, title), el('ul', { class: 'lobby-seats' }, seatRow(left, 'Left · Lanes 1–2'), seatRow(right, 'Right · Lanes 3–4')));
  showScreen(
    menuShell(
      el('h2', { class: 'menu-sub' }, '2v2 game'),
      el('div', { class: 'game-code' }, code),
      el('p', { class: 'muted' }, full ? (host ? 'Every seat is filled. Start when you’re ready.' : 'Every seat is filled. Waiting for the host to start…') : `Waiting for players… Send them this code, or they can join from Open Games. Anyone can move to an empty seat.${host ? ' You can fill any empty seat with a bot.' : ''}`),
      el('div', { class: 'lobby-teams' }, team('Team 1', 0, 2), team('Team 2', 1, 3)),
      el('p', { class: 'muted small' }, 'Which team goes first (and plays blue) is picked at random when the game starts.'),
      // everyone in the lobby can talk while the seats fill (the same chat carries on at the table)
      el('section', { class: 'lobby-chat' }, el('h3', { class: 'menu-sub-small' }, 'Chat'), lobbyChat(conn).root),
      el(
        'div',
        { class: 'menu-buttons' },
        ...(host ? [button('Start Game', () => conn.startGame(), 'btn-big btn-primary', full ? {} : { disabled: true })] : []),
        copy,
        host ? button('Cancel', () => conn.cancel(), 'btn-ghost') : button('Leave', () => { conn.close(true); showVersusMenu(); }, 'btn-ghost'),
      ),
    ),
  );
}

/** The bots, weakest first: name, difficulty out of 9 and how it plays. */
function botLabel(b: (typeof BOTS)[number]) {
  return `${b.name} · ${b.rank}/9`;
}
/** 1v1 lobby: a list of the bots to fill the empty seat with. */
function botPicker(title: string, pick: (level: string) => void) {
  return el(
    'details',
    { class: 'bot-picker' },
    el('summary', { class: 'btn btn-big' }, title),
    el('div', { class: 'bot-list' }, BOTS.map((b) =>
      el('button', { class: 'btn bot-choice', type: 'button', onclick: () => pick(b.id) },
        el('span', { class: 'bot-head' },
          el('span', { class: 'bot-name' }, b.name),
          // difficulty: a row of nine pips, filled up to its rank, and the number
          el('span', { class: 'bot-rank', title: `Difficulty ${b.rank} of ${BOTS.length}` },
            el('span', { class: 'bot-pips', 'aria-hidden': 'true' }, BOTS.map((_, i) => el('span', { class: i < b.rank ? 'pip on' : 'pip' }))),
            el('span', { class: 'bot-rank-num' }, `${b.rank}/${BOTS.length}`))),
        el('span', { class: 'bot-blurb' }, b.blurb)))),
  );
}
/** 2v2 lobby: a small menu on an empty seat to put a bot there. */
function botSelect(pick: (level: string) => void) {
  const sel = el('select', { class: 'bot-select', 'aria-label': 'Fill this seat with a bot' }, el('option', { value: '' }, 'Add a bot…'), BOTS.map((b) => el('option', { value: b.id, title: b.blurb }, botLabel(b))));
  sel.addEventListener('change', () => { const v = (sel as HTMLSelectElement).value; if (v) pick(v); });
  return sel;
}

function showJoinByCode() {
  const input = el('input', { class: 'text-input code-input', maxlength: String(CODE_LENGTH + 2), placeholder: 'Game code', autocomplete: 'off', spellcheck: 'false' });
  const go = () => {
    const code = cleanCode((input as HTMLInputElement).value);
    if (code.length !== CODE_LENGTH) {
      toast(`A game code has ${CODE_LENGTH} letters and numbers.`, 'error');
      return;
    }
    joinGame(code);
  };
  input.addEventListener('keydown', (e) => (e as KeyboardEvent).key === 'Enter' && go());
  showScreen(
    menuShell(
      el('h2', { class: 'menu-sub' }, 'Join with a Code'),
      el('p', { class: 'muted' }, 'If that game is already being played, you watch it instead.'),
      input,
      el('div', { class: 'menu-buttons' }, button('Join', go, 'btn-big btn-primary'), button('Back', () => showVersusMenu(), 'btn-ghost')),
    ),
  );
  input.focus();
}

/**
 * Join a game by its code. If it is already being played, the server lets you watch it instead (no deck needed
 * for that); a game still waiting for its second player needs a deck, so the deck builder opens if you have none.
 */
function joinGame(code: string) {
  stopLobby();
  // a game this browser already has a seat in (it was refreshed or closed): its seat is taken back, not watched
  const saved = savedOnlineGame();
  if (saved && saved.code.toUpperCase() === code.trim().toUpperCase()) {
    const conn = new OnlineConnection({ rejoin: saved });
    connecting(conn, 'Taking your seat back…');
    return;
  }
  const conn = new OnlineConnection({ join: { code, name: session.playerName, deck: session.savedDeck ?? new Map() } });
  connecting(conn, 'Joining the game…', (reason) => {
    if (reason !== NEED_DECK) return false;
    needDeck(() => joinGame(code));
    return true;
  });
}

function rejoinGame() {
  const saved = savedOnlineGame();
  if (!saved) return showVersusMenu();
  stopLobby();
  const conn = new OnlineConnection({ rejoin: saved });
  connecting(conn, 'Taking your seat back…');
}

function connecting(conn: OnlineConnection, what: string, handled?: (reason: string) => boolean) {
  conn.onError((m) => m !== NEED_DECK && toast(m, 'error'));
  conn.onClosed((reason) => {
    if (handled?.(reason)) return;
    toast(reason, 'error');
    showVersusMenu();
  });
  conn.onRoom((started) => (started ? openOnlineTable(conn) : drawLobby(conn)));
  showScreen(menuShell(el('h2', { class: 'menu-sub' }, what), el('div', { class: 'menu-buttons' }, button('Back', () => { conn.close(); showVersusMenu(); }, 'btn-ghost'))));
}

let tableOpen: OnlineConnection | null = null;
function openOnlineTable(conn: OnlineConnection) {
  if (tableOpen === conn) return; // the server says "started" again after a reconnect: the table is already up
  tableOpen = conn;
  lobbyChats.get(conn)?.dispose(); // (the table has its own chat box: the lobby's is done with)
  lobbyChats.delete(conn);
  versusTeams = !!conn.teams; // leaving the table goes back to the menu for this kind of game
  startTable({
    names: conn.names,
    online: conn,
    onExit: () => {
      tableOpen = null;
      showVersusMenu();
    },
    onMenu: () => {
      tableOpen = null;
      showMainMenu();
    },
  });
}

/**
 * Do you have a deck to play with? If not, the deck builder opens, and saving a deck there carries
 * on with `then`: the game mode you had already chosen, so you aren't asked for it twice.
 */
function needDeck(then: () => void): boolean {
  if (session.savedDeck) return true;
  toast('You need a deck first (60 cards + 1 Emperor). Save one and your game starts right away.', 'info');
  showDeckBuilder(showModeSelect, then);
  return false;
}

function startSolo() {
  if (!needDeck(startSolo)) return;
  startTable({
    names: [session.playerName],
    deck: session.savedDeck!,
    onExit: showModeSelect,
    onMenu: showMainMenu,
  });
}

/** Opens a replay from a file on the player's computer. */
async function loadMatch() {
  const picked = await pickTextFile(`${MATCH_EXTENSION},.json,application/json`);
  if (!picked) return;
  const file = parseMatchFile(picked.text);
  if (typeof file === 'string') {
    toast(file, 'error');
    return;
  }
  // (games aren't saved part-way any more: an older file of a game saved part-way is watched up to where it stopped)
  startTable({ names: file.names, replay: { file }, onExit: showMainMenu });
}
